import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DEVICE_WIDTH, parsePreviewMessage, previewOrigin, studioMessage, type BridgeTarget, type Device } from './bridge';
import type { Selection } from './StandInPreview';
import s from './designer.module.css';

type Props = {
  url: string;
  device: Device;
  pick: boolean;
  selection: Selection;
  onSelect: (sel: Selection, target: BridgeTarget) => void;
  label: string;
};

/**
 * The variant's preview build in an iframe on the preview origin (P3-C01), talking to the Studio
 * through the postMessage bridge. AC-12: the URL must be another origin than the Studio's (no shared
 * cookies or storage), the frame is sandboxed without top navigation, popups or forms, it gets no
 * referrer, and only messages from that origin and that frame are read (bridge.ts validates them).
 * The frame is the device's viewport: it fills the stage and the page scrolls inside it, as in a
 * browser. (Stretching the frame to the page's height made every `100vh` section as tall as the
 * frame, which grew the page, which grew the frame: the content ended up thousands of pixels down.)
 * The selection outline is drawn by the page itself (`highlight`), so it scrolls with the content.
 */
export function PreviewFrame({ url, device, pick, selection, onSelect, label }: Props) {
  const { t } = useTranslation('designer');
  const frame = useRef<HTMLIFrameElement>(null);
  const origin = previewOrigin(url, window.location.origin);
  const width = DEVICE_WIDTH[device];
  const selRef = useRef(selection);
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    selRef.current = selection;
    onSelectRef.current = onSelect;
  });

  const post = (type: Parameters<typeof studioMessage>[0], payload: Record<string, unknown>) => {
    if (origin) frame.current?.contentWindow?.postMessage(studioMessage(type, payload), origin);
  };

  useEffect(() => {
    if (!origin) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== origin || e.source !== frame.current?.contentWindow) return;
      const m = parsePreviewMessage(e.data);
      if (!m) return;
      if (m.type === 'ready') highlight();
      if (m.type === 'select') {
        const target = m.payload.target;
        // Element selection works inside the selected section (C3); elsewhere a click selects the section.
        const inside = !!target.el && selRef.current.section === target.section_id;
        const next: Selection = { section: target.section_id, el: inside ? (target.el ?? null) : null };
        onSelectRef.current(next, target);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin]);

  // The selected section (or element) is outlined inside the page while «Выделить» is on.
  const highlight = () => {
    const sel = selRef.current;
    post('highlight', { target: pickRef.current && sel.section ? { section_id: sel.section, el: sel.el ?? null } : null });
  };
  const pickRef = useRef(pick);
  useEffect(() => {
    pickRef.current = pick;
    highlight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick, selection.section, selection.el]);

  // «Выделить» and the device go to the preview as they change.
  useEffect(() => {
    post('mode', { mode: pick ? 'element' : 'off' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick]);
  useEffect(() => {
    post('device', { device, width: width ?? Math.round(frame.current?.getBoundingClientRect().width ?? 1280) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  if (!origin) return <div className={s.refused}>{t('preview.refused')}</div>;
  return (
    <div className={s.frameWrap} style={{ width: width ? `${width}px` : '100%' }}>
      <iframe
        ref={frame}
        className={s.frame}
        src={url}
        title={label}
        sandbox="allow-scripts allow-same-origin"
        referrerPolicy="no-referrer"
        onLoad={() => {
          post('init', { mode: pick ? 'element' : 'off' });
          highlight();
        }}
      />
    </div>
  );
}
